'use client';

import React, { Suspense, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, OrbitControls, Environment, Center } from '@react-three/drei';
import * as THREE from 'three';
import { useIntersectionObserver } from './hooks/useIntersectionObserver';

interface AvatarModelProps {
  url: string;
}

function AvatarModel({ url }: AvatarModelProps) {
  const { scene } = useGLTF(url);
  const modelRef = useRef<THREE.Group>(null);

  // Add subtle rotation animation
  useFrame((state) => {
    if (modelRef.current) {
      modelRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.2) * 0.05;
    }
  });

  return (
    <Center>
      <primitive 
        ref={modelRef}
        object={scene} 
        scale={[1.2, 1.2, 1.2]}
        position={[0, -0.5, 0]}
      />
    </Center>
  );
}

function LoadingFallback() {
  return (
    <mesh>
      <boxGeometry args={[1, 2, 0.5]} />
      <meshStandardMaterial color="#6366f1" />
    </mesh>
  );
}

interface AvatarViewer3DProps {
  modelUrl: string;
  className?: string;
  autoRotate?: boolean;
  showControls?: boolean;
}

export default function AvatarViewer3D({ 
  modelUrl, 
  className = '',
  autoRotate = false,
  showControls = true
}: AvatarViewer3DProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isVisible = useIntersectionObserver(containerRef, {
    threshold: 0.1,
    rootMargin: '100px'
  });
  
  const [isLoaded, setIsLoaded] = useState(false);

  // Only render 3D content when visible
  if (!isVisible) {
    return (
      <div 
        ref={containerRef} 
        className={`w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center ${className}`}
      >
        <div className="text-gray-500 text-sm">Scroll to load 3D model</div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`w-full h-64 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg overflow-hidden ${className}`}
    >
      <Canvas
        camera={{ 
          position: [0, 0, 4], 
          fov: 50,
          near: 0.1,
          far: 1000
        }}
        style={{ width: '100%', height: '100%' }}
        gl={{ antialias: true, alpha: true }}
        onCreated={() => setIsLoaded(true)}
      >
        {/* Lighting */}
        <ambientLight intensity={0.7} />
        <directionalLight 
          position={[5, 5, 5]} 
          intensity={0.8}
          castShadow
        />
        <pointLight position={[-5, -5, -5]} intensity={0.2} />

        {/* Environment for better lighting */}
        <Environment preset="city" />

        {/* 3D Model with proper Suspense */}
        <Suspense fallback={<LoadingFallback />}>
          <AvatarModel url={modelUrl} />
        </Suspense>

        {/* Controls */}
        {showControls && (
          <OrbitControls 
            enablePan={false}
            enableZoom={true}
            enableRotate={true}
            autoRotate={autoRotate}
            autoRotateSpeed={1}
            minDistance={2}
            maxDistance={6}
            minPolarAngle={Math.PI / 6}
            maxPolarAngle={Math.PI - Math.PI / 6}
          />
        )}
      </Canvas>

      {/* Loading indicator */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      )}
    </div>
  );
}